import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /node_instances/:node_instance_id/wait_events (routes/node_instances.js). The endpoint returns
// the two wait-event sources as separate arrays, each in its native shape and enriched with the
// resolved event identity + `raised`:
//   - node_wait_events: the node_instance_events snapshot (regular nodes).
//   - flow_wait_events: flow_instance_nodes.wait_events JSONB entries (events declared on a flow node),
//     referenced by a 'Zone|EVENT' path.
// This endpoint validates both wire arrays; the getNodeInstanceWaitEvents operation flattens them into
// one canonical, source-tagged WaitEvent list.
//
// `raised` means "a raised event_instances row exists for this event" — the same predicate the
// scheduler uses to satisfy a wait. It deliberately ignores `precondition`: a wait whose precondition
// is false (incl. a skipped wait) is treated as already passed even with no raised instance, so
// `raised` can read false while the node is not actually blocked on it. `precondition` is surfaced
// verbatim so the operator can interpret a grey badge.
//
// Flow `event_id`/`event_type` are null when a path does not resolve to a known event; such rows still
// render by name but without an instances link.

export interface NodeWaitEventWire {
  readonly node_instance_events_id: number;
  readonly event_id: number;
  readonly event_name: string;
  readonly event_type: string;
  readonly zone_id: number;
  readonly zone_name: string;
  // `node_instance_events.auto_submit` / `.auto_clear` are nullable columns with no default, and this route selects them
  // raw. `raised` is `nie.is_satisfied`, likewise nullable — null there means the wait has not been evaluated yet, which
  // is not the same as evaluated-and-unsatisfied even though both leave the node waiting.
  readonly auto_submit: boolean | null;
  readonly auto_clear: boolean | null;
  readonly scheduling_window_id: number | null;
  readonly precondition: string | null;
  readonly raised: boolean | null;
}

export interface FlowWaitEventWire {
  readonly path: string;
  readonly event_id: number | null;
  readonly event_name: string;
  readonly event_type: string | null;
  readonly zone_name: string;
  readonly auto_submit: boolean;
  readonly auto_clear: boolean;
  readonly scheduling_window: string | null;
  readonly precondition: string | null;
  readonly raised: boolean;
}

const nodeWaitEventWireSchema = object<NodeWaitEventWire>({
  node_instance_events_id: num(REJECT),
  event_id: num(),
  event_name: str(),
  event_type: str(),
  zone_id: num(),
  zone_name: str(),
  auto_submit: nullable(bool()),
  auto_clear: nullable(bool()),
  scheduling_window_id: nullable(num()),
  precondition: nullable(str()),
  raised: nullable(bool()),
});

// NOT audited against a live wire: the route hardcodes `flow_wait_events: []` (routes/node_instances.js), so this branch
// never runs today and there is nothing to check these declarations against. They are also not backed by the
// `node_instance_events` columns above — the comment at the top of this file records the intended source as
// `flow_instance_nodes.wait_events` JSONB entries, where the analogous definition-level type declares `auto_submit` as
// OPTIONAL rather than nullable. Whoever revives the branch should re-derive this shape rather than trust it.
const flowWaitEventWireSchema = object<FlowWaitEventWire>({
  // How a flow wait event is addressed — an identity in all but name.
  path: str(REJECT),
  event_id: nullable(num()),
  event_name: str(),
  event_type: nullable(str()),
  zone_name: str(),
  auto_submit: bool(),
  auto_clear: bool(),
  scheduling_window: nullable(str()),
  precondition: nullable(str()),
  raised: bool(),
});

export interface WaitEventsResponseWire {
  readonly node_wait_events: readonly NodeWaitEventWire[];
  readonly flow_wait_events: readonly FlowWaitEventWire[];
}

const waitEventsResponseSchema = object<WaitEventsResponseWire>({
  node_wait_events: array(nodeWaitEventWireSchema),
  flow_wait_events: array(flowWaitEventWireSchema),
});

/**
 * Fetches the raw wait-event wire for a node instance (the two source arrays). Throws only if the
 * response envelope is structurally malformed; rows whose identity drifts are dropped individually.
 */
export const getNodeInstanceWaitEventsWire = async (nodeInstanceId: number | string): Promise<Received<WaitEventsResponseWire>> => {
  const res = await api.get(`/node_instances/${encodeURIComponent(String(nodeInstanceId))}/wait_events`);
  return readResponse(res.data, {
    schema: waitEventsResponseSchema,
    source: 'getNodeInstanceWaitEventsWire',
  });
};
